import { type CSSProperties, type RefObject, useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { ChromaKeyVideo } from '@/components/food-cost/ChromaKeyVideo'
import {
    ASSETS,
    CHARACTER_SECTION_HEIGHTS,
    type CharacterEntryHeight,
    SIDE_CHROMA_KEY_SETTINGS,
} from '@/components/food-cost/constants'
import { getVisibleHeightRatio } from '@/lib/dom/visibility'

type FoodCostSideCharacterProps = {
    sectionRef: RefObject<HTMLElement | null>
    heroRef: RefObject<HTMLElement | null>
}

type CharacterLayout = {
    activeIndex: number
    height: CharacterEntryHeight
}

const SIDE_HORIZONTAL_OFFSET = { mobile: 22, desktop: 24 }

const IDLE_REPLAY_DELAY_MS = 10_000
const HERO_HIDE_THRESHOLD = 0.8
const HERO_SHOW_THRESHOLD = 0.72

const VERTICAL_POSITION: Record<CharacterEntryHeight, CSSProperties> = {
    bottom: { bottom: '4%', top: 'auto' },
    center: { top: '46%', bottom: 'auto' },
    top: { top: '10%', bottom: 'auto' },
}

function getPositionStyle(height: CharacterEntryHeight, isMobile: boolean): CSSProperties {
    const offset = isMobile ? SIDE_HORIZONTAL_OFFSET.mobile : SIDE_HORIZONTAL_OFFSET.desktop

    return {
        right: 0,
        left: 'auto',
        transform: `translateX(${offset}%)`,
        transformOrigin: 'right center',
        ...VERTICAL_POSITION[height],
    }
}

function resolveActiveSectionIndex(sections: HTMLElement[], viewportHeight: number): number {
    const anchorY = viewportHeight * 0.44

    for (let index = 0; index < sections.length; index++) {
        const rect = sections[index].getBoundingClientRect()
        if (rect.top <= anchorY && rect.bottom >= anchorY) return index
    }

    let closestIndex = 0
    let closestDistance = Number.POSITIVE_INFINITY

    sections.forEach((section, index) => {
        const rect = section.getBoundingClientRect()
        if (rect.bottom < 0 || rect.top > viewportHeight) return

        const sectionCenter = rect.top + rect.height / 2
        const distance = Math.abs(sectionCenter - anchorY)

        if (distance < closestDistance) {
            closestDistance = distance
            closestIndex = index
        }
    })

    return closestIndex
}

export function FoodCostSideCharacter({ sectionRef, heroRef }: FoodCostSideCharacterProps) {
    const { t } = useTranslation()
    const [mounted, setMounted] = useState(false)
    const [displayVisible, setDisplayVisible] = useState(false)
    const [videoMounted, setVideoMounted] = useState(false)
    const [replayNonce, setReplayNonce] = useState(0)
    const [layout, setLayout] = useState<CharacterLayout>({
        activeIndex: 0,
        height: CHARACTER_SECTION_HEIGHTS[0],
    })
    const [isMobile, setIsMobile] = useState(false)

    const scrollWantsVisibleRef = useRef(false)
    const wasShowingRef = useRef(false)
    const hasEverShownRef = useRef(false)
    const heroBlocksRef = useRef(false)
    const loopLockRef = useRef(false)
    const playbackKeyRef = useRef('')
    const pendingHeightRef = useRef<CharacterEntryHeight | null>(null)
    const replayTimeoutRef = useRef<number | null>(null)
    const lastScrollAtRef = useRef(0)

    const clearReplayTimeout = useCallback(() => {
        if (replayTimeoutRef.current === null) return
        window.clearTimeout(replayTimeoutRef.current)
        replayTimeoutRef.current = null
    }, [])

    useEffect(() => {
        setMounted(true)
    }, [])

    const tryHide = useCallback(() => {
        if (loopLockRef.current || scrollWantsVisibleRef.current) return
        setDisplayVisible(false)
    }, [])

    const handleLoopComplete = useCallback(() => {
        loopLockRef.current = false
        setDisplayVisible(false)
        clearReplayTimeout()

        if (!scrollWantsVisibleRef.current) {
            pendingHeightRef.current = null
            return
        }

        replayTimeoutRef.current = window.setTimeout(() => {
            if (!scrollWantsVisibleRef.current) return
            if (Date.now() - lastScrollAtRef.current < IDLE_REPLAY_DELAY_MS) return

            loopLockRef.current = true

            if (pendingHeightRef.current) {
                const height = pendingHeightRef.current
                pendingHeightRef.current = null
                setLayout(current => ({ ...current, height }))
            }
            setReplayNonce(current => current + 1)
            setDisplayVisible(true)
        }, IDLE_REPLAY_DELAY_MS)
    }, [clearReplayTimeout])

    useEffect(() => {
        const update = () => {
            lastScrollAtRef.current = Date.now()
            clearReplayTimeout()

            const container = sectionRef.current
            if (!container) return

            const sections = Array.from(container.querySelectorAll<HTMLElement>('[data-character-section]'))
            if (sections.length === 0) return

            const viewportHeight = window.innerHeight
            const heroElement = heroRef.current

            if (heroElement) {
                const heroVisibleRatio = getVisibleHeightRatio(heroElement.getBoundingClientRect(), viewportHeight)

                if (heroVisibleRatio > HERO_HIDE_THRESHOLD) heroBlocksRef.current = true
                else if (heroVisibleRatio < HERO_SHOW_THRESHOLD) heroBlocksRef.current = false

                if (heroBlocksRef.current) {
                    scrollWantsVisibleRef.current = false
                    wasShowingRef.current = false
                    loopLockRef.current = false
                    pendingHeightRef.current = null
                    playbackKeyRef.current = ''
                    clearReplayTimeout()
                    setDisplayVisible(false)
                    return
                }
            }

            const nextIsMobile = window.innerWidth < 768
            setIsMobile(nextIsMobile)
            // Mobile skips the character: its video is the heaviest asset on the page.
            if (nextIsMobile) {
                scrollWantsVisibleRef.current = false
                wasShowingRef.current = false
                setDisplayVisible(false)
                return
            }
            const firstRect = sections[0].getBoundingClientRect()
            const lastRect = sections[sections.length - 1].getBoundingClientRect()

            const hasEntered = firstRect.top < viewportHeight * 0.88
            const hasNotExited = lastRect.bottom > viewportHeight * 0.06
            const sectionInView = sections.some(section => {
                const rect = section.getBoundingClientRect()
                return rect.bottom > 0 && rect.top < viewportHeight
            })

            const shouldShow = hasEntered && hasNotExited && sectionInView
            scrollWantsVisibleRef.current = shouldShow

            if (!shouldShow) {
                wasShowingRef.current = false
                tryHide()
                return
            }

            if (!hasEverShownRef.current) {
                hasEverShownRef.current = true
                setVideoMounted(true)
            }

            if (!wasShowingRef.current) {
                loopLockRef.current = true
            }
            wasShowingRef.current = true

            const activeIndex = resolveActiveSectionIndex(sections, viewportHeight)
            const height = CHARACTER_SECTION_HEIGHTS[activeIndex % CHARACTER_SECTION_HEIGHTS.length]
            const playbackKey = String(activeIndex)
            const sectionChanged = playbackKeyRef.current !== playbackKey
            const animationPlaying = loopLockRef.current

            if (sectionChanged) {
                const wasAlreadyPlaying = playbackKeyRef.current !== ''
                playbackKeyRef.current = playbackKey

                if (animationPlaying && wasAlreadyPlaying) {
                    pendingHeightRef.current = height
                    setLayout(current => ({ ...current, activeIndex }))
                } else {
                    pendingHeightRef.current = null
                    loopLockRef.current = true
                    setLayout({ activeIndex, height })
                }
            } else if (!pendingHeightRef.current) {
                setLayout(current => ({ ...current, activeIndex, height }))
            } else {
                setLayout(current => ({ ...current, activeIndex }))
            }

            setDisplayVisible(true)
        }

        update()
        window.addEventListener('scroll', update, { passive: true })
        window.addEventListener('resize', update)
        return () => {
            clearReplayTimeout()
            window.removeEventListener('scroll', update)
            window.removeEventListener('resize', update)
        }
    }, [clearReplayTimeout, heroRef, sectionRef, tryHide])

    if (!mounted) return null

    const positionStyle = getPositionStyle(layout.height, isMobile)

    const overlay = (
        <div
            className='pointer-events-none fixed z-[45] w-[min(74vw,340px)] md:w-[min(50vw,540px)]'
            style={{
                ...positionStyle,
                opacity: displayVisible ? 1 : 0,
                visibility: displayVisible ? 'visible' : 'hidden',
                transition: displayVisible ? 'opacity 0.35s ease-out' : 'none',
            }}
            aria-hidden={!displayVisible}
        >
            {videoMounted ? (
                <ChromaKeyVideo
                    key={`side-character-${replayNonce}`}
                    src={ASSETS.videoEnteringFromRightSource}
                    poster={ASSETS.videoEnteringFromRightPoster}
                    settings={SIDE_CHROMA_KEY_SETTINGS}
                    preload='none'
                    onLoopComplete={handleLoopComplete}
                    aria-label={t('foodCost.sideCharacter.animationAriaLabel')}
                    className='w-full'
                />
            ) : null}
        </div>
    )

    return createPortal(overlay, document.body)
}
